// ── Time Calendar Utilities ──────────────────────────────────────────────────
// Constants, color assignment, overlap layout, and positioning helpers
// for the weekly drag-and-drop time tracking calendar view.

export const HOUR_HEIGHT = 60;       // px per hour row
export const START_HOUR = 0;
export const END_HOUR = 24;
export const TOTAL_HOURS = 24;
export const BUSINESS_START = 7;     // auto-scroll to this on mount
export const BUSINESS_END = 20;
export const SNAP_MINUTES = 5;       // 5-minute drag snap
export const MIN_BLOCK_MINUTES = 5;  // minimum drag-create duration

// ── Matter Color Palette ─────────────────────────────────────────────────────

export interface MatterColor {
  bg: string;
  border: string;
  text: string;
  hex: string;
}

const MATTER_COLORS: MatterColor[] = [
  { bg: "bg-blue-100",    border: "border-l-blue-500",    text: "text-blue-700",    hex: "#3B82F6" },
  { bg: "bg-violet-100",  border: "border-l-violet-500",  text: "text-violet-700",  hex: "#8B5CF6" },
  { bg: "bg-amber-100",   border: "border-l-amber-500",   text: "text-amber-700",   hex: "#F59E0B" },
  { bg: "bg-rose-100",    border: "border-l-rose-500",    text: "text-rose-700",    hex: "#F43F5E" },
  { bg: "bg-teal-100",    border: "border-l-teal-500",    text: "text-teal-700",    hex: "#14B8A6" },
  { bg: "bg-pink-100",    border: "border-l-pink-500",    text: "text-pink-700",    hex: "#EC4899" },
  { bg: "bg-cyan-100",    border: "border-l-cyan-500",    text: "text-cyan-700",    hex: "#06B6D4" },
  { bg: "bg-indigo-100",  border: "border-l-indigo-500",  text: "text-indigo-700",  hex: "#6366F1" },
  { bg: "bg-orange-100",  border: "border-l-orange-500",  text: "text-orange-700",  hex: "#F97316" },
  { bg: "bg-emerald-100", border: "border-l-emerald-500", text: "text-emerald-700", hex: "#10B981" },
];

export function getMatterColor(matterId: string): MatterColor {
  let hash = 0;
  for (let i = 0; i < matterId.length; i++) {
    hash = matterId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return MATTER_COLORS[Math.abs(hash) % MATTER_COLORS.length];
}

// ── Positioning ──────────────────────────────────────────────────────────────

export interface EntryPosition {
  top: number;   // px from top of grid
  height: number; // px height
}

export function getEntryPosition(
  startTime: string | null | undefined,
  minutes: number,
  timerStartedAt?: string | null
): EntryPosition {
  const height = Math.max((minutes / 60) * HOUR_HEIGHT, 15);

  if (startTime) {
    const [h, m] = startTime.split(":").map(Number);
    const startMinutes = h * 60 + m;
    return { top: (startMinutes / 60) * HOUR_HEIGHT, height };
  }

  // Fallback: derive from timer.startedAt
  if (timerStartedAt) {
    const d = new Date(timerStartedAt);
    const startMinutes = d.getHours() * 60 + d.getMinutes();
    return { top: (startMinutes / 60) * HOUR_HEIGHT, height };
  }

  // No time info: unscheduled
  return { top: -1, height };
}

// ── Overlap Layout ───────────────────────────────────────────────────────────
// Adapted from CalendarPage.computeOverlapLayout

interface OverlapEntry {
  _id: string;
  startMinutes: number;
  endMinutes: number;
}

export function computeTimeEntryOverlap(
  entries: OverlapEntry[]
): Map<string, { left: number; width: number }> {
  const layout = new Map<string, { left: number; width: number }>();
  if (entries.length === 0) return layout;

  const sorted = [...entries].sort((a, b) => {
    const diff = a.startMinutes - b.startMinutes;
    if (diff !== 0) return diff;
    return b.endMinutes - a.endMinutes; // longer entries first
  });

  const groups: OverlapEntry[][] = [];
  let currentGroup: OverlapEntry[] = [];
  let groupEnd = 0;

  for (const entry of sorted) {
    if (currentGroup.length === 0 || entry.startMinutes < groupEnd) {
      currentGroup.push(entry);
      groupEnd = Math.max(groupEnd, entry.endMinutes);
    } else {
      groups.push(currentGroup);
      currentGroup = [entry];
      groupEnd = entry.endMinutes;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  for (const group of groups) {
    const columns: OverlapEntry[][] = [];
    for (const entry of group) {
      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        const lastInCol = columns[col][columns[col].length - 1];
        if (lastInCol.endMinutes <= entry.startMinutes) {
          columns[col].push(entry);
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([entry]);
      }
    }
    const totalCols = columns.length;
    for (let col = 0; col < columns.length; col++) {
      for (const entry of columns[col]) {
        layout.set(entry._id, {
          left: (col / totalCols) * 100,
          width: 100 / totalCols,
        });
      }
    }
  }
  return layout;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function snapToGrid(minutes: number, snap: number = SNAP_MINUTES): number {
  return Math.round(minutes / snap) * snap;
}

export function formatHourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

export function minutesToTimeStr(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function getWeekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

export function formatShortDate(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatWeekday(d: Date): string {
  return d.toLocaleDateString("en-US", { weekday: "short" });
}

export function toISODateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
